// Membros da equipe (tabela `profiles`), hidratados em memória no login —
// mesmo padrão dos demais caches. Existe porque a delegação de tarefas precisa
// resolver `tasks.assignee_id` (uuid) para nome/avatar em tempo de render, sem
// transformar cada card do kanban numa consulta ao banco.
import { supabase } from "@/integrations/supabase/client";

export interface Membro {
  id: string;
  nome: string;
  email: string | null;
  avatarUrl: string | null;
}

let _membros: Membro[] = [];
let _porId = new Map<string, Membro>();

function rowToMembro(row: {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url?: string | null;
}): Membro {
  return {
    id: row.id,
    nome: row.full_name || row.email || "Usuário sem nome",
    email: row.email ?? null,
    avatarUrl: row.avatar_url ?? null,
  };
}

export async function hydrateProfiles(): Promise<void> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name, email, avatar_url");
  if (error) {
    console.error("hydrateProfiles:", error.message);
    return;
  }
  _membros = (data ?? [])
    .map(rowToMembro)
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" }));
  _porId = new Map(_membros.map((m) => [m.id, m]));
}

/** Todos os membros, em ordem alfabética. */
export const listMembros = (): Membro[] => _membros;

export const getMembroById = (id?: string | null): Membro | undefined =>
  id ? _porId.get(id) : undefined;

/**
 * Nome de exibição de um responsável. Cai para um rótulo neutro quando o perfil
 * ainda não foi hidratado — nunca devolve o uuid cru para a tela.
 */
export const getMembroNome = (id?: string | null): string | undefined => {
  if (!id) return undefined;
  return _porId.get(id)?.nome ?? "Responsável";
};

/** Iniciais para avatar (uma ou duas letras). */
export const getIniciais = (nome: string): string => {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
};
